import React, { useState, useEffect } from 'react';
import { Button, Modal, Dropdown } from 'react-bootstrap';
import { 
  FaTrash, FaPen, FaPlus, FaList, FaMousePointer, FaCog, 
  FaCheck, FaTimes, FaFolder, FaFolderOpen, FaChevronDown, FaChevronRight,
  FaEye, FaEyeSlash, FaLevelUpAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";

// Import components
import UpdateCategory from './components/UpdateCategory/UpdateCategory';
import CreateCategory from './components/CreateCategory/CreateCategory';
import DeleteCategory from './components/DeleteCategory/DeleteCategory';
import RemoveCategoryFromMenu from './components/RemoveCategoryFromMenu/RemoveCategoryFromMenu';
import ModalMenuCategory from './components/MenuManagement/MenuManagement';
import PromoteSubcategoryModal from './components/PromoteSubcategoryModal/PromoteSubcategoryModal';
import Table from '../../../Menu/components/Items/components/Table/Table';

// Import types and APIs
import { ParentsCategorys, RawParentsCategorys, SubCategoryType } from '../../types';
import { Item } from '../../../Menu/types';
import { getWorkstationsByLocalId } from 'crud/management_api/workstations';
import { 
  updateCategory, 
  deleteCategory, 
  getSubCategoriesByCategoryId,
  toggleIsMainCategory,
  updateWorkstationOfCategory
} from '../../../../../../crud/menu_api/categories';
// import { 
//   getMenusByLocalId 
// } from '../../../../../../crud/menu_api/menus';
import { 
  getAllItemsByLocalId,
  getItemsByMenuCategoryId 
} from '../../../../../../crud/menu_api/items';

import './HierarchicalTable.scss';

interface HierarchicalTableProps {
  categories: RawParentsCategorys[];
  setCategories: (data: RawParentsCategorys[]) => void;
  originalCategories: ParentsCategorys[];
  setOriginalCategories: (data: ParentsCategorys[]) => void;
  setSearchTerm?: (term: string) => void;
  selectMode: boolean;
  setSelectMode: (value: boolean) => void;
  showCreateCategoryModal: boolean;
  setShowCreateCategoryModal: (value: boolean) => void;
}

interface ExpandedState {
  [categoryId: number]: boolean;
}

interface SubcategoriesCache {
  [categoryId: number]: SubCategoryType[];
}

const HierarchicalTable: React.FC<HierarchicalTableProps> = ({
  categories,
  setCategories,
  originalCategories,
  setOriginalCategories,
  setSearchTerm = () => {},
  selectMode,
  setSelectMode,
  showCreateCategoryModal,
  setShowCreateCategoryModal,
}) => {
  // State management
  const [currentData, setCurrentData] = useState<RawParentsCategorys[]>(categories);
  const [expandedCategories, setExpandedCategories] = useState<ExpandedState>({});
  const [subcategoriesCache, setSubcategoriesCache] = useState<SubcategoriesCache>({});
  const [loadingSubcategories, setLoadingSubcategories] = useState<Set<number>>(new Set());

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showRemoveMenuModal, setShowRemoveMenuModal] = useState(false);
  const [showMenuCategoryModal, setShowMenuCategoryModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  // Current selection states
  const [currentCategory, setCurrentCategory] = useState<RawParentsCategorys | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  // Promote modal data
  const [promoteSubcategoryId, setPromoteSubcategoryId] = useState<number>(0);
  const [promoteSubcategoryName, setPromoteSubcategoryName] = useState<string>('');
  const [promoteParentCategoryId, setPromoteParentCategoryId] = useState<number>(0);
  const [promoteParentCategoryName, setPromoteParentCategoryName] = useState<string>('');
  // Selection mode (now from props)
  const [selectedCategories, setSelectedCategories] = useState<RawParentsCategorys[]>([]);

  // Items view states
  const [showItemsView, setShowItemsView] = useState(false);
  const [selectedCategoryForItems, setSelectedCategoryForItems] = useState<number | null>(null);
  const [selectedCategoryNameForItems, setSelectedCategoryNameForItems] = useState<string>('');
  const [itemsData, setItemsData] = useState<Item[]>([]);
  const [originalItemsData, setOriginalItemsData] = useState<Item[]>([]);
  const [allItemsData, setAllItemsData] = useState<Item[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  // Workstations
  const [workstations, setWorkstations] = useState<Array<{ workstation_id: number; workstation_name: string }>>([]);
  // Pending toggles for is_main to avoid concurrent updates
  const [pendingIsMain, setPendingIsMain] = useState<Set<number>>(new Set());

  const navigate = useNavigate();

  // Effects
  useEffect(() => {
    setCurrentData(categories);
  }, [categories]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load workstations for this local
  useEffect(() => {
    const loadWorkstations = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const localId = user.local_id || 0;
        const resp = await getWorkstationsByLocalId(localId);
        setWorkstations(resp.data || []);
      } catch (err) {
        console.error('Error loading workstations', err);
      }
    };
    loadWorkstations();
  }, []);

  // Helper to change workstation for a parent category
  const changeCategoryWorkstation = async (catId: number, newId: number | null) => {
    // Optimistic update
    setCurrentData((prev: RawParentsCategorys[]) => prev.map(cat => cat.category_id === catId ? ({ ...cat, workstation_id: newId, workstation_name: workstations.find(w => w.workstation_id === newId)?.workstation_name || null } as RawParentsCategorys) : cat));
    setOriginalCategories(originalCategories.map((cat: ParentsCategorys) => cat.category_id === catId ? ({ ...cat, workstation_id: newId, workstation_name: workstations.find(w => w.workstation_id === newId)?.workstation_name || null } as ParentsCategorys) : cat));
    
    try {
      await updateWorkstationOfCategory(catId, newId?? 0 );
      toast.success('Estación de trabajo actualizada');
    } catch (err) {
      console.error('Error updating workstation for category', err);
      toast.error('Error al actualizar estación de trabajo');
    }
  };

  // Helper to change workstation for a subcategory
  const changeSubcategoryWorkstation = async (parentId: number, subCatId: number, newId: number | null) => {
    // Update subcategories cache so UI reflects change
    setSubcategoriesCache((prev: SubcategoriesCache) => {
      const copy: SubcategoriesCache = { ...prev };
      if (copy[parentId]) {
        copy[parentId] = copy[parentId].map(sc => sc.category_id === subCatId ? ({ ...sc, workstation_id: newId, workstation_name: workstations.find(w => w.workstation_id === newId)?.workstation_name || null } as SubCategoryType) : sc);
      }
      return copy;
    });

    try {
      await updateWorkstationOfCategory(subCatId, newId);
      toast.success('Estación de trabajo actualizada');
    } catch (err) {
      console.error('Error updating workstation for subcategory', err);
      toast.error('Error al actualizar estación de trabajo');
    }
  };

  // Toggle is_main (visibility) for parent or subcategory
  const toggleIsMainLocal = async (categoryId: number, parentId?: number) => {
    // avoid duplicate requests
    if (pendingIsMain.has(categoryId)) return;

    // If parentId is provided, we're toggling a subcategory
    if (parentId !== undefined) {
      const prevValue = subcategoriesCache[parentId]?.find(sc => sc.category_id === categoryId)?.is_main;
      if (typeof prevValue === 'undefined') return;

      // Optimistic update
      setSubcategoriesCache(prev => {
        const copy: SubcategoriesCache = { ...prev };
        if (copy[parentId]) {
          copy[parentId] = copy[parentId].map(sc => sc.category_id === categoryId ? ({ ...sc, is_main: !prevValue } as SubCategoryType) : sc);
        }
        return copy;
      });

      setPendingIsMain(prev => new Set(prev).add(categoryId));
      try {
        await toggleIsMainCategory(categoryId);
        toast.success('Visibilidad actualizada');
      } catch (err) {
        // rollback
        setSubcategoriesCache(prev => {
          const copy: SubcategoriesCache = { ...prev };
          if (copy[parentId]) {
            copy[parentId] = copy[parentId].map(sc => sc.category_id === categoryId ? ({ ...sc, is_main: prevValue } as SubCategoryType) : sc);
          }
          return copy;
        });
        console.error('Error toggling is_main for subcategory', err);
        toast.error('Error al actualizar visibilidad');
      } finally {
        setPendingIsMain(prev => {
          const copy = new Set(prev);
          copy.delete(categoryId);
          return copy;
        });
      }

      return;
    }

    // Parent category toggle
    const prevValue = currentData.find(cat => cat.category_id === categoryId)?.is_main;
    if (typeof prevValue === 'undefined') return;

  // Optimistic update on currentData and originalCategories
  setCurrentData(prev => prev.map(cat => cat.category_id === categoryId ? ({ ...cat, is_main: !prevValue } as RawParentsCategorys) : cat));
  setOriginalCategories(originalCategories.map(cat => cat.category_id === categoryId ? ({ ...cat, is_main: !prevValue } as any) : cat));

    setPendingIsMain(prev => new Set(prev).add(categoryId));
    try {
      await toggleIsMainCategory(categoryId);
      toast.success('Visibilidad actualizada');
    } catch (err) {
  // rollback
  setCurrentData(prev => prev.map(cat => cat.category_id === categoryId ? ({ ...cat, is_main: prevValue } as RawParentsCategorys) : cat));
  setOriginalCategories(originalCategories.map(cat => cat.category_id === categoryId ? ({ ...cat, is_main: prevValue } as any) : cat));
      console.error('Error toggling is_main for category', err);
      toast.error('Error al actualizar visibilidad');
    } finally {
      setPendingIsMain(prev => {
        const copy = new Set(prev);
        copy.delete(categoryId);
        return copy;
      });
    }
  };

  // Utility functions
  const toggleItemState = (id: number) => {
    const current_state = currentData.find(category => category.category_id === id)?.state;
    updateCategory(id, { state: !current_state })
      .then((response) => {
        const updatedParentCategory: ParentsCategorys = (response as any).data;
        const updatedOriginalCategory = originalCategories.filter(cat => cat.category_id !== id);
        const newOriginalIngredients = [updatedParentCategory, ...updatedOriginalCategory];
        setOriginalCategories(newOriginalIngredients);
        setCategories(newOriginalIngredients);
        setCurrentData(newOriginalIngredients);
        toast.success('Estado de la categoría se ha actualizado con éxito');
      })
      .catch((error) => {
        console.error('Error al actualizar el estado de la categoría:', error);
        toast.error('Error al actualizar el estado de la categoría');
      });
  };

  const handleShowDeleteModal = (category: RawParentsCategorys) => {
    setCurrentCategory(category);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = (category: RawParentsCategorys) => {
    if (category) {
      setCurrentCategory(category);
      setShowEditModal(true);
    } else {
      toast.error('La categoría no puede ser editada');
    }
  };

  const toggleCategoryExpansion = async (categoryId: number) => {
    const isCurrentlyExpanded = expandedCategories[categoryId];
    
    if (!isCurrentlyExpanded && !subcategoriesCache[categoryId]) {
      // Load subcategories if not cached
      setLoadingSubcategories(prev => new Set([...prev, categoryId]));
      try {
        const response = await getSubCategoriesByCategoryId(categoryId);
        setSubcategoriesCache(prev => ({
          ...prev,
          [categoryId]: response.data
        }));
      } catch (error) {
        console.error('Error loading subcategories:', error);
        toast.error('Error al cargar subcategorías');
        return;
      } finally {
        setLoadingSubcategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(categoryId);
          return newSet;
        });
      }
    }

    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !isCurrentlyExpanded
    }));
  };
  const toggleCategorySelection = (category: RawParentsCategorys) => {
    setSelectedCategories(prevState => {
      if (prevState.some(selected => selected.category_id === category.category_id)) {
        return prevState.filter(selected => selected.category_id !== category.category_id);
      } else {
        return [...prevState, category];
      }
    });
  };

  // Function to load items for a specific category
  const loadItemsForCategory = async (categoryId: number, categoryName: string) => {
    // Set loading state immediately for instant feedback
    setSelectedCategoryForItems(categoryId);
    setLoadingItems(true);
    setSelectedCategoryNameForItems(categoryName);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = Number(user.local_id) || 0;

      // Try to get items by category first
      let itemsResponse;
      try {
        itemsResponse = await getItemsByMenuCategoryId(categoryId);
      } catch (error) {
        // Fallback: get all items and filter manually
        const allItemsResponse = await getAllItemsByLocalId(localId);
        const filteredItems = allItemsResponse.data.filter((item: Item) => 
          item.category_id === String(categoryId)
        );
        itemsResponse = { data: filteredItems };
      }

      // Also get all items for context
      const allItemsResponse = await getAllItemsByLocalId(localId);

      // Show view immediately for better perceived performance
      setShowItemsView(true);
      
      if (itemsResponse?.data) {
        const sortedItems = itemsResponse.data.sort((a: Item, b: Item) => a.label.localeCompare(b.label));
        setItemsData(sortedItems);
        setOriginalItemsData(sortedItems);
        setAllItemsData(allItemsResponse.data || []);
        
      } else {
        setShowItemsView(true);
        setItemsData([]);
        setOriginalItemsData([]);
        setAllItemsData(allItemsResponse.data || []);
        toast.info(`No se encontraron items en "${categoryName}"`);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Error al cargar los items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleGoBackToCategories = () => {
    setShowItemsView(false);
    setSelectedCategoryForItems(null);
    setSelectedCategoryNameForItems('');
    setItemsData([]);
    setOriginalItemsData([]);
    setAllItemsData([]);
  };
  // const handleShowAddMenuModal = () => {
  //   const userContext = localStorage.getItem("user");
  //   const localId = userContext ? JSON.parse(userContext).local_id : 0;
  //   getMenusByLocalId(localId)
  //     .then((response: any) => {
  //       // For now, just show success message - modal functionality will be added later
  //       toast.success("Menús cargados. Funcionalidad en desarrollo.");
  //     })
  //     .catch((error: any) => {
  //       console.error("Error fetching menus:", error);
  //       toast.error("Error al obtener los menús.");
  //     });
  // };

  const handleMenuCategoryRemoved = (menuId: number, categoryId: number) => {
    const updatedCategories = categories.map((category: RawParentsCategorys) => {
      if (category.category_id === categoryId) {
        return {
          ...category,
          menus: Array.isArray(category.menus)
            ? category.menus.filter((menu: any) => menu.menu_id !== menuId)
            : [],
        };
      }
      return category;
    });
    setCategories(updatedCategories);
  };

  const handleShowPromoteModal = (subcategory: SubCategoryType, parentCategory: RawParentsCategorys) => {
    setPromoteSubcategoryId(subcategory.category_id);
    setPromoteSubcategoryName(subcategory.category_name);
    setPromoteParentCategoryId(parentCategory.category_id);
    setPromoteParentCategoryName(parentCategory.category_name);
    setShowPromoteModal(true);
  };

  const handlePromoteSuccess = () => {
    // Refresh categories from server to get updated structure
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;
    
    // Clear subcategories cache for the parent
    setSubcategoriesCache(prev => {
      const newCache = { ...prev };
      delete newCache[promoteParentCategoryId];
      return newCache;
    });
    
    // Collapse the parent category to force a refresh
    setExpandedCategories(prev => ({
      ...prev,
      [promoteParentCategoryId]: false
    }));
    
    // Refresh the main categories list
    import('../../../../../../crud/menu_api/categories').then(({ getAllParentsCategoriesByLocalId }) => {
      getAllParentsCategoriesByLocalId(localId)
        .then(response => {
          setCategories(response.data);
          setOriginalCategories(response.data);
          setCurrentData(response.data);
        })
        .catch(error => {
          console.error("Error refreshing categories:", error);
        });
    });
  };

  const renderCategoryIcon = (category: RawParentsCategorys, isExpanded: boolean = false) => {
    if (category.isParent) {
      return isExpanded ? 
        <FaFolderOpen className="category-icon parent-open" /> : 
        <FaFolder className="category-icon parent-closed" />;
    }
    return <FaList className="category-icon terminal" />;
  };

  const renderExpandButton = (category: RawParentsCategorys) => {
    if (!category.isParent) return null;
    
    const isExpanded = expandedCategories[category.category_id];
    const isLoading = loadingSubcategories.has(category.category_id);
    
    return (
      <button 
        className="expand-button"
        onClick={() => toggleCategoryExpansion(category.category_id)}
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="mini-spinner" />
        ) : isExpanded ? (
          <FaChevronDown />
        ) : (
          <FaChevronRight />
        )}
      </button>
    );
  };

  const renderCompositionAction = (category: RawParentsCategorys) => {
    if (category.isParent) {
      return (
        <button className="mp-button border">
          <FaFolder />
        </button>
      );
    } else {
      return (
        <button
          className="mp-button border"
          onClick={() => loadItemsForCategory(category.category_id, category.category_name)}
          disabled={loadingItems}
        >
          {loadingItems && selectedCategoryForItems === category.category_id ? (
            <div className="mini-spinner" />
          ) : (
            <FaList />
          )}
        </button>
      );
    }
  };

  const renderMenusCell = (category: RawParentsCategorys) => {
    if (isMobile) {
      return category.menus && category.menus.length > 0 ? (
        <span 
          className="table-item-menus-categorys mobile" 
          onClick={() => {
            setSelectedCategoryName(category.category_name);
            setSelectedCategoryId(category.category_id);
            setShowMenuCategoryModal(true);
          }}
        >
          <FaCheck style={{ color: 'var(--mediopunto-orange)' }} />
        </span>
      ) : (
        <span 
          onClick={() => {
            setSelectedCategoryName(category.category_name);
            setSelectedCategoryId(category.category_id);
            setShowMenuCategoryModal(true);
          }}
          className="table-item-menus-categorys mobile add"
        >
          +
        </span>
      );
    }

    return (
      <div className="menu-badges-container">
        {category.menus && category.menus.length > 0 ? 
          category.menus.map((menu, index) => (
            <span key={index} className="menu-badge">
              {menu.menu_name || `Menú ${index + 1}`}
            </span>
          ))
          : null
        }
        <button
          className="add-menu-button"
          onClick={() => {
            setSelectedCategoryName(category.category_name);
            setSelectedCategoryId(category.category_id);
            setShowMenuCategoryModal(true);
          }}
        >
          +
        </button>
      </div>
    );
  };

  const renderSubcategoryRow = (subcategory: SubCategoryType, parentId: number) => (
    <tr 
      key={`sub-${subcategory.category_id}`} 
      className={`subcategory-row clickable`}
      onClick={(e) => {
        // Only trigger if clicking on the row itself, not on buttons or interactive elements
        const target = e.target as HTMLElement;
        if (
          !target.closest('button') && 
          !target.closest('input') && 
          !target.closest('.dropdown')
        ) {
          loadItemsForCategory(subcategory.category_id, subcategory.category_name);
        }
      }}
    >
      {selectMode && <td className='td-select-checkbox'></td>}
      <td className="category-name subcategory">
        <div className="category-name-container">
          <div className="subcategory-indent">
            <div className="subcategory-connector"></div>
            <FaList className="category-icon subcategory" />
            <span>
              {subcategory.category_name}
              {subcategory.menu_category_type_name && (
                <span className="category-type">({subcategory.menu_category_type_name})</span>
              )}
            </span>
          </div>
        </div>
      </td>
      <td className='no-responsive'>
        <label className="switch">
          <input
            className="toggle-switch-item-checkbox"
            type="checkbox"
            checked={subcategory.state}
            onChange={() => toggleItemState(subcategory.category_id)}
          />
          <span className="slider round"></span>
        </label>
      </td>
      <td className='no-responsive'>
        <div className="visibility-icon">
          <button
            className="visibility-toggle mp-button"
            onClick={() => toggleIsMainLocal(subcategory.category_id, parentId)}
            disabled={pendingIsMain.has(subcategory.category_id)}
            aria-label={subcategory.is_main ? 'Ocultar subcategoría' : 'Mostrar subcategoría'}
          >
            {subcategory.is_main ? 
              <FaEye className="visible" /> : 
              <FaEyeSlash className="hidden" />
            }
          </button>
        </div>
      </td>      <td className='no-responsive'>
        <Dropdown className="workstation-dropdown">
          <Dropdown.Toggle variant="" className="workstation-dropdown-toggle">
            {subcategory.workstation_name || 'No asignado'}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => changeSubcategoryWorkstation(parentId, subcategory.category_id, null)}>
              No asignado
            </Dropdown.Item>
            {workstations.map(w => (
              <Dropdown.Item key={w.workstation_id} onClick={() => changeSubcategoryWorkstation(parentId, subcategory.category_id, w.workstation_id)}>
                {w.workstation_name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </td>
      <td>
        <span className="subcategory-inherited">Hereda del padre</span>
      </td>
      <td className="action-button-table-container">
        <div className="action-buttons-wrapper">
          <button 
            className="mp-button" 
            onClick={() => {
              const parentCategory = currentData.find(cat => cat.category_id === parentId);
              if (parentCategory) {
                handleShowPromoteModal(subcategory, parentCategory);
              }
            }}
            title="Promover a categoría principal"
          >
            <FaLevelUpAlt />
          </button>
          <button className="mp-button" onClick={() => handleShowEditModal(subcategory as any)}>
            <FaPen />
          </button>
          <button className="mp-button delete-button" onClick={() => handleShowDeleteModal(subcategory as any)}>
            <FaTrash />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      {currentData.length === 0 ? (
        <div className="no-data-available">
          <div className="no-data-available-message">
            ¡No has creado ninguna categoría aún!
          </div>
          <Button className="no-data-available-button" onClick={() => setShowCreateCategoryModal(true)}>
            <FaPlus /> Crear categoría
          </Button>
        </div>
      ) : showItemsView ? (
        // Items view
        <div className="items-view-container">
          <Table
            items={itemsData}
            categoryId={selectedCategoryForItems ? String(selectedCategoryForItems) : undefined}
            setData={setItemsData}
            originalItems={originalItemsData}
            setOriginalItems={setOriginalItemsData}
            allItems={allItemsData}
            onGoBack={handleGoBackToCategories}
          />
        </div>
      ) : (
        // Categories view
        <div className="hierarchical-table-container">
        {selectedCategories.length > 0 && (
          <div className="action-button-table-container">
            <Button 
              variant='none' 
              className="mp-button border" 
              onClick={async () => {
                const selectedIds = selectedCategories.map(cat => cat.category_id);
                try {
                  await Promise.all(selectedIds.map(id => deleteCategory(id)));
                  const newCategories = categories.filter(cat => !selectedIds.includes(cat.category_id));
                  setCategories(newCategories);
                  setSelectedCategories([]);
                  setCurrentData(newCategories);
                  toast.success('Selección eliminada');
                } catch (error) {
                  toast.error('Error al eliminar una o más categorías');
                }
              }}
            >
              <FaTrash/> Eliminar selección ({selectedCategories.length})
            </Button>
          </div>
        )}

        <div className="scroll-container-table">
          <table className="hierarchical-table">
            <thead>
              <tr>
                {selectMode && <th className='no_pagination'></th>}
                <th className='no_pagination'>Nombre</th>
                <th className='no_pagination no-responsive'>Estado</th>
                <th className='no_pagination no-responsive'>Visible</th>
                <th className='no_pagination no-responsive'>
                  {isMobile ? 'Estación trabajo' : 'Estación de trabajo'}
                </th>
                <th className='no_pagination'>Menús</th>
                <th className='no_pagination'>
                  {isMobile ? <FaCog /> : 'Acciones'}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentData.map(category => [
                // Parent category row
                <tr 
                  key={category.category_id} 
                  className={`parent-category-row ${!category.isParent ? 'clickable' : ''}`}
                  onClick={(e) => {
                    // Only trigger if clicking on the row itself, not on buttons or interactive elements
                    const target = e.target as HTMLElement;
                    if (
                      !target.closest('button') && 
                      !target.closest('input') && 
                      !target.closest('.dropdown') &&
                      !target.closest('.mp-radio-button') &&
                      !category.isParent
                    ) {
                      loadItemsForCategory(category.category_id, category.category_name);
                    }
                  }}
                  style={!category.isParent ? { cursor: 'pointer' } : {}}
                >
                  {selectMode && (
                    <td className='td-select-checkbox'>
                      <div className="mp-radio-button">
                        <input
                          id={`checkbox-${category.category_id}`}
                          type="checkbox"
                          checked={selectedCategories.some((selected) => selected.category_id === category.category_id)}
                          onChange={() => toggleCategorySelection(category)}
                        />
                        <label htmlFor={`checkbox-${category.category_id}`}></label>
                      </div>
                    </td>
                  )}
                  <td className="category-name">
                    <div className="category-name-container">
                      {renderExpandButton(category)}
                      {renderCategoryIcon(category, expandedCategories[category.category_id])}
                      <span>
                        {category.category_name}
                        {category.menu_category_type_name && (
                          <span className="category-type">({category.menu_category_type_name})</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className='no-responsive'>
                    <label className="switch">
                      <input
                        className="toggle-switch-item-checkbox"
                        type="checkbox"
                        checked={category.state}
                        onChange={() => toggleItemState(category.category_id)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </td>
                  <td className='no-responsive'>
                    <div className="visibility-icon">
                      <button
                        className="visibility-toggle mp-button"
                        onClick={() => toggleIsMainLocal(category.category_id)}
                        disabled={pendingIsMain.has(category.category_id)}
                        aria-label={category.is_main ? 'Ocultar categoría' : 'Mostrar categoría'}
                      >
                        {category.is_main ? 
                          <FaEye className="visible" /> : 
                          <FaEyeSlash className="hidden" />
                        }
                      </button>
                    </div>
                  </td>
                  <td className='no-responsive'>
                    <Dropdown className="workstation-dropdown">
                      <Dropdown.Toggle variant="" className="workstation-dropdown-toggle">
                        {category.workstation_name || 'No asignado'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => changeCategoryWorkstation(category.category_id, null)}>
                          No asignado
                        </Dropdown.Item>
                        {workstations.map(w => (
                          <Dropdown.Item key={w.workstation_id} onClick={() => changeCategoryWorkstation(category.category_id, w.workstation_id)}>
                            {w.workstation_name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td>
                    {renderMenusCell(category)}
                  </td>
                  <td className="action-button-table-container">
                    <div className="action-buttons-wrapper">
                      <button className="mp-button" onClick={() => handleShowEditModal(category)}>
                        <FaPen />
                      </button>
                      <button className="mp-button delete-button" onClick={() => handleShowDeleteModal(category)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>,
                // Subcategory rows (if expanded and has subcategories)
                ...(expandedCategories[category.category_id] && subcategoriesCache[category.category_id] 
                  ? subcategoriesCache[category.category_id].map(sub => 
                      renderSubcategoryRow(sub, category.category_id)
                    ) 
                  : []
                )
              ])}
            </tbody>
          </table>        </div>
      </div>
      )}

      {/* Modals */}
      {showCreateCategoryModal && (
        <CreateCategory
          showCreateModal={showCreateCategoryModal}
          setShowCreateModal={setShowCreateCategoryModal}
          setCategories={setCategories}
          setOriginalCategories={setOriginalCategories}
          currentData={currentData}
          setCurrentData={setCurrentData}
          originalCategories={originalCategories}
        />
      )}

      {showEditModal && currentCategory && (
        <UpdateCategory
          showEditModal={showEditModal}
          setShowEditModal={setShowEditModal}
          currentData={currentData}
          setCurrentData={setCurrentData}
          currentCategory={currentCategory}
          originalCategories={originalCategories}
          setOriginalCategories={setOriginalCategories}
          setCategories={setCategories}
        />
      )}

      {showDeleteModal && currentCategory && (
        <DeleteCategory
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={setShowDeleteModal}
          currentData={currentData}
          setCurrentData={setCurrentData}
          currentCategory={currentCategory}
          originalCategories={originalCategories}
          setOriginalCategories={setOriginalCategories}
          setCategories={setCategories}
        />
      )}

      {showOptionsModal && (
        <Modal show={showOptionsModal} onHide={() => setShowOptionsModal(false)} centered className="empty-category-modal">
          <Modal.Header className="empty-category-modal-header">
            <div className="form-modal-title-container">
              <Modal.Title className="form-modal-title">Categoría Vacía</Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="empty-category-modal-body">
            <p>
              La categoría está vacía. Recuerda que las categorías pueden contener <em>items</em> o <em>subcategorías</em> pero no ambos.
            </p>
            <p>Por favor, elige una opción para continuar:</p>
            <div className="options-grid">
              <div 
                className="option-card items-option"
                onClick={() => {
                  navigate(`/App/Items/${selectedCategoryId}`);
                  setShowOptionsModal(false);
                }}
              >
                <FaList className="option-icon" />
                <span className="option-title">Agregar items</span>
                <p className="option-description">
                  Convierte esta categoría en terminal para agregar productos
                </p>
              </div>
              <div 
                className="option-card subcategories-option"
                onClick={() => {
                  // Handle subcategory creation logic
                  setShowOptionsModal(false);
                  // Navigate to subcategory management or show create subcategory modal
                }}
              >
                <FaFolder className="option-icon" />
                <span className="option-title">Agregar subcategoría</span>
                <p className="option-description">
                  Convierte esta categoría en padre para organizar subcategorías
                </p>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {showMenuCategoryModal && (
        <ModalMenuCategory
          show={showMenuCategoryModal}
          onClose={() => setShowMenuCategoryModal(false)}
          category={currentData.find(cat => cat.category_id === selectedCategoryId)}
        />
      )}

      <RemoveCategoryFromMenu
        showDeleteModal={showRemoveMenuModal}
        setShowDeleteModal={setShowRemoveMenuModal}
        selectedCategories={selectedCategoryName ? [selectedCategoryName] : []}
        selectedCategoryId={selectedCategoryId}
        selectedmenuId={null}
        onMenuCategoryRemoved={handleMenuCategoryRemoved}
      />

      {showPromoteModal && (
        <PromoteSubcategoryModal
          show={showPromoteModal}
          onHide={() => setShowPromoteModal(false)}
          subcategoryId={promoteSubcategoryId}
          subcategoryName={promoteSubcategoryName}
          parentCategoryName={promoteParentCategoryName}
          onSuccess={handlePromoteSuccess}
        />
      )}
    </>
  );
};

export default HierarchicalTable;
